import { AfterViewInit, Component, HostListener, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { Store } from "@ngxs/store";
import { filter, map } from "rxjs";
import { WebsocketProvider } from "@/common/providers/websocket.provider";
import { SystemStore, WebsocketConnectAction, WebsocketDisconnectAction } from "@/common/stores/system.store";
import { SITE_INFO } from "@shared/site.constants";
import { MENU_DISPLAY_TYPES, MENU_GROUPS, MenuItem } from "@shared/menu.constants";
import { AuthService } from "./common/services/auth.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, AfterViewInit {
  ready: boolean = false;
  connected: boolean = false;
  authenticated: boolean = false;
  navOpen: boolean = false;
  pageLogo: any = SITE_INFO.logo;
  pageTitle: string = SITE_INFO.pageTitle;
  copyright: string = SITE_INFO.copyright;
  copyrightDate: Date = new Date();
  clientVersion: string = "";
  serverVersion: string = "";
  cssClass: string = "";
  headerMenu: MenuItem[] = [];
  footerMenu: MenuItem[] = [];

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private websocketProvider: WebsocketProvider,
    private authService: AuthService,
  ) {}

  @HostListener("window:resize")
  onResize(): void {
    this.resizeViewport();
  }

  ngOnInit(): void {
    // Resize viewport
    this.resizeViewport();

    // Update the page title
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter(() => {
          let child = this.activatedRoute.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          if (child && child.snapshot.data['title']) {
            return true;
          }
          return false;
        }),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          if (child && child.snapshot.data['title']) {
            return `${this.pageTitle} - ${child.snapshot.data['title']}`;
          }
          return this.pageTitle;
        })
      )
      .subscribe((title: string) => {
        this.titleService.setTitle(title);
      });

    // Close the navigation if a page is changed to
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.navOpen = false;
        this.cssClass = "";
        if (this.activatedRoute.firstChild && this.activatedRoute.firstChild.snapshot.data) {
          this.cssClass = this.activatedRoute.firstChild.snapshot.data.cssClass || "";
        }
      });

    this.store.select(SystemStore.versions)
      .subscribe(versions => {
        this.clientVersion = versions.clientVersion;
        this.serverVersion = versions.serverVersion;
      });

    this.authService.user$
      .subscribe(authenticated => {
        this.authenticated = authenticated
        this.getMenuItems();
      });

    this.store.select(SystemStore.menus)
      .subscribe(() => this.getMenuItems());

    this.websocketProvider.on("connect", () => {
      this.checkConnection();
    });

    setInterval(() => {
      this.checkConnection();
    }, 5000);
  }

  ngAfterViewInit(): void {
    this.ready = true;
  }

  resizeViewport(): void {
    const app = document.getElementById("app");
    if (app) {
      app.style.height = `${window.innerHeight - 1}px`;
    }
  }

  checkConnection(): void {
    const connected = this.websocketProvider.socket.connected;
    if (!this.connected && connected) {
      this.connected = true;
      this.store.dispatch(new WebsocketConnectAction()).subscribe();
    } else if (this.connected && !connected) {
      this.connected = false;
      this.store.dispatch(new WebsocketDisconnectAction()).subscribe();
    }
  }

  getMenuItems(): void {
    this.headerMenu = this.store.selectSnapshot(SystemStore.menu(MENU_GROUPS.Header))
      .filter(this.filterMenuItem.bind(this));

    this.footerMenu = this.store.selectSnapshot(SystemStore.menu(MENU_GROUPS.Footer))
      .filter(this.filterMenuItem.bind(this));
  }

  filterMenuItem(menuItem: MenuItem): boolean {
    // Requires authentication
    if (menuItem.display) {
      if (menuItem.display === MENU_DISPLAY_TYPES.Authenticated && !this.authenticated) return false;
      if (menuItem.display === MENU_DISPLAY_TYPES.Anonymous && this.authenticated) return false;
      if (menuItem.display === MENU_DISPLAY_TYPES.Always) return true;
    }
    return this.authService.isAuthorized(menuItem.roles);
  }
}
